<template>
  <div v-if="comments.length > 0 || showNewComment" class="mt-3 border-0">
    <div style="height: 31px">
      <span class="border-bottom border-secondary inline-block" style="width: 200px">
        {{ t("components.eventAction.task.TaskCommentSection.title") }}
        <div class="flex">
          <hr class="w-100" />
          <i
            v-if="moreCommentsAvailable"
            class="fa ml-1 cursor-pointer"
            :class="{ 'fa-chevron-down': !showAllComments, 'fa-chevron-up': showAllComments }"
            @click.stop="showAllComments = !showAllComments"
          />
        </div>
      </span>
    </div>
    <TaskRevisionList :comments="comments" />
    <TaskCreateComment
      v-if="showNewComment && archived === false && task.id != null"
      :task-id="task.id"
      class="mt-2"
      :original-board-id="originalBoardId"
      @comment-added="showNewComment = false"
    />
  </div>
</template>

<script lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, defineComponent, type PropType, ref, getCurrentInstance } from "vue";
import mitt from "mitt";
import TaskCreateComment from "./TaskCreateComment.vue";
import TaskRevisionList from "./TaskRevisionList.vue";
import { useRevisions } from "./useRevisions";
import type { EditTaskType } from "./helper/DefaultTask";
import type { ActionListActionFields_Task_Fragment } from "@/graphql";

type Events = {
  // A new comment screen is shown by the vue instance with uid <number>
  showNewComment: number;
};

export const taskCommentEmitter = mitt<Events>();

export default defineComponent({
  name: "TaskCommentSection",
  components: {
    TaskCreateComment,
    TaskRevisionList,
  },
  props: {
    task: {
      type: Object as PropType<EditTaskType | ActionListActionFields_Task_Fragment>,
      required: true,
    },
    archived: {
      type: Boolean,
      required: false,
      default: false,
    },
    originalBoardId: {
      type: Number,
      required: false,
      default: undefined,
    },
  },
  setup(props) {
    const { t } = useTranslation();
    const MINIMUM_NUMBER_OF_COMMENTS = 5;

    const showAllComments = ref((props.task.Revisions?.length ?? 0) <= MINIMUM_NUMBER_OF_COMMENTS);

    const showNewComment = ref(false);
    const instance = getCurrentInstance();
    taskCommentEmitter.on("showNewComment", (e) => {
      if (e !== instance?.uid) {
        showNewComment.value = false;
      }
    });
    function startCommenting() {
      showNewComment.value = true;
      taskCommentEmitter.emit("showNewComment", instance?.uid ?? -1);
    }
    const revisions = computed(() => props.task.Revisions ?? []);

    const allComments = useRevisions(revisions);
    const comments = computed(() => {
      return (
        allComments.value?.filter((_, index, theArray) => {
          if (showAllComments.value) {
            return true;
          }
          return index >= theArray.length - MINIMUM_NUMBER_OF_COMMENTS;
        }) ?? []
      );
    });

    const moreCommentsAvailable = computed(
      () => allComments.value.length > MINIMUM_NUMBER_OF_COMMENTS,
    );

    return {
      t,
      comments,
      showAllComments,
      showNewComment,
      startCommenting,
      moreCommentsAvailable,
    };
  },
});
</script>

<style lang="scss" scoped>
.commentGrid {
  display: grid;
  gap: 4px;
  grid-template-columns: min-content max-content auto;
  align-items: baseline;

  .date {
    white-space: nowrap;
  }
}
</style>
